import { inject } from '@angular/core';
import { selectSelectedAccountId } from '@mkp/account/state';
import {
  ApplicationResource,
  ApplicationStatusResource,
  mapApplicationDtoToModel,
} from '@mkp/application/data-access';
import { Application, ApplicationStep, getStatusIdForStep } from '@mkp/application/models';
import {
  applicationApiActions,
  applicationExistGuardActions,
  applicationPageActions,
  applicationStatusApiActions,
  applicationStatusesGuardActions,
  hasMissingApplications,
} from '@mkp/application/state/actions';
import { getHttpErrorResponse } from '@mkp/shared/util-format';
import { ofTypeWithFilter } from '@mkp/shared/util-state';
import { Vacancy } from '@mkp/vacancy/models';
import { selectVacancyFromRoute, selectVacancyIdFromRoute } from '@mkp/vacancy/state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  map,
  of,
  OperatorFunction,
  switchMap,
  take,
} from 'rxjs';
import { updateManyActions, updateOneActions } from './application.reducer';
import {
  selectApplications,
  selectApplicationStatusesForSelectedAccount,
} from './application.selectors';

export const loadRouteApplication = createEffect(
  (actions$ = inject(Actions), applicationResource = inject(ApplicationResource)) => {
    return actions$.pipe(
      ofType(applicationExistGuardActions.canActivate),
      map(({ id }) => id),
      filter(Boolean),
      exhaustMap((id) =>
        applicationResource.getById(id).pipe(
          map(mapApplicationDtoToModel),
          map((application) =>
            applicationApiActions.routeApplicationLoadedSuccess({ application })
          ),
          catchError((error: unknown) =>
            of(
              applicationApiActions.routeApplicationLoadedFailure({
                errorResponse: getHttpErrorResponse(error),
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const fetchCounts = createEffect(
  (
    actions$ = inject(Actions),
    applicationResource = inject(ApplicationResource),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofTypeWithFilter(
        applicationPageActions.opened,
        applicationPageActions.stepChanged,
        applicationPageActions.reloadNewTabAfterApplicationAdded,
        applicationApiActions.applicationRefreshNotFound,
        applicationApiActions.applicationsDeleteEnded,
        applicationPageActions.searchedApplicationSelected,
        ...updateManyActions,
        ...getUpdateOneActionsForCountsTrigger(),
        {
          action: applicationApiActions.applicationsReadRefreshSuccess,
          filter: hasMissingApplications,
        }
      ),
      concatLatestFrom(() => [
        store.select(selectApplicationStatusesForSelectedAccount),
        store.select(selectVacancyFromRoute),
      ]),
      filter(hasVacancyInThirdPosition),
      exhaustMap(([, statuses, { id: vacancyId, accountId }]) =>
        applicationResource.fetchApplicationCounts({ statuses, vacancyId, accountId }).pipe(
          map((applicationCounts) =>
            applicationApiActions.applicationCountsLoadedSuccess({ applicationCounts })
          ),
          catchError((error: unknown) =>
            of(
              applicationApiActions.applicationCountsLoadedFailure({
                errorResponse: getHttpErrorResponse(error),
              })
            )
          )
        )
      )
    ),
  {
    functional: true,
  }
);

export const refreshApplication = createEffect(
  (actions$ = inject(Actions), applicationResource = inject(ApplicationResource)) => {
    return actions$.pipe(
      ofType(
        applicationPageActions.userSelectApplication,
        applicationPageActions.selectApplicationOnChange
      ),
      map(({ applicationId }) => applicationId),
      filter(Boolean),
      concatMap((applicationId) => {
        return applicationResource.getById(applicationId).pipe(
          map(mapApplicationDtoToModel),
          map((application) => applicationApiActions.applicationRefreshSuccess({ application })),
          catchError((error: unknown) => {
            const errorResponse = getHttpErrorResponse(error);
            if (errorResponse.status === 404) {
              return of(applicationApiActions.applicationRefreshNotFound({ applicationId }));
            }
            return of(
              applicationApiActions.applicationRefreshFailure({
                errorResponse,
              })
            );
          })
        );
      })
    );
  },
  { functional: true }
);

export const loadApplicationStatuses = createEffect(
  (
    store = inject(Store),
    actions$ = inject(Actions),
    applicationStatusResource = inject(ApplicationStatusResource)
  ) => {
    return actions$.pipe(
      ofType(applicationStatusesGuardActions.canActivate),
      exhaustMap(() => store.select(selectSelectedAccountId).pipe(filter(Boolean), take(1))),
      concatLatestFrom(() => store.select(selectApplicationStatusesForSelectedAccount)),
      filter(([, applicationStatuses]) => applicationStatuses.length === 0),
      exhaustMap(([selectedAccountId]) =>
        applicationStatusResource.list(selectedAccountId).pipe(
          map((applicationStatuses) =>
            applicationStatusApiActions.applicationStatusesLoadedSuccess({ applicationStatuses })
          ),
          catchError((error: unknown) =>
            of(
              applicationStatusApiActions.applicationStatusesLoadedFailure({
                errorResponse: getHttpErrorResponse(error),
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadApplications = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        applicationPageActions.opened,
        applicationPageActions.stepChanged,
        applicationPageActions.reloadNewTabAfterApplicationAdded,
        applicationPageActions.reloadNewTabAfterApplicationAdded
      ),
      loadMoreApplications(
        applicationApiActions.applicationsLoadedSuccess,
        applicationApiActions.applicationsLoadedFailure
      )
    ),
  { functional: true }
);

// Only load when the searched app is not already in the list
export const loadApplicationsOnSearch = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(applicationPageActions.searchedApplicationSelected),
      concatLatestFrom(() => store.select(selectApplications)),
      filter(isSearchedApplicationNotLoaded),
      map(([action]) => action),
      loadMoreApplications(
        applicationApiActions.applicationsLoadedForSearchedApplicationSuccess,
        applicationApiActions.applicationsLoadedForSearchedApplicationFailure
      )
    ),
  { functional: true }
);

export const loadMoreApplicationsWithSeparator = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(applicationPageActions.loadMoreButtonClicked),
      loadMoreApplications(
        applicationApiActions.moreApplicationsLoadedSuccess,
        applicationApiActions.moreApplicationsLoadedFailure
      )
    );
  },
  { functional: true }
);

export const loadMoreApplicationsAfterChange = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(applicationPageActions.loadMoreApplicationsAfterChange),
      loadMoreApplications(
        applicationApiActions.moreApplicationsLoadedAfterChangeSuccess,
        applicationApiActions.moreApplicationsLoadedAfterChangeFailure
      )
    );
  },
  { functional: true }
);

export const loadMoreApplicationsForSearchedApplication = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(applicationPageActions.loadMoreApplicationsForSearchedApplication),
      loadMoreApplications(
        applicationApiActions.moreApplicationsLoadedForSearchedApplicationSuccess,
        applicationApiActions.moreApplicationsLoadedForSearchedApplicationFailure
      )
    );
  },
  { functional: true }
);

// TODO - migrate this from global-effects into the signal-store
export const reloadApplicationForStatusAlreadyChanged = createEffect(
  (actions$ = inject(Actions), applicationResource = inject(ApplicationResource)) => {
    return actions$.pipe(
      ofType(applicationApiActions.applicationsUpdateEnded),
      filter(({ errors409 }) => errors409.length > 0),
      exhaustMap(({ successes, errors409 }) =>
        applicationResource.getByIds(errors409.map(({ applicationId }) => applicationId)).pipe(
          map((reloadedApplications) =>
            applicationApiActions.applicationsReloadForStatusAlreadyChangedSuccess({
              successes,
              reloadedApplications,
            })
          ),
          catchError((error: unknown) =>
            of(
              applicationApiActions.applicationReloadForStatusAlreadyChangedFailure({
                errorResponse: getHttpErrorResponse(error),
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

type LoadOneOrMoreApplicationsSuccessActionCreator =
  | typeof applicationApiActions.applicationsLoadedSuccess
  | typeof applicationApiActions.applicationsLoadedForSearchedApplicationSuccess
  | typeof applicationApiActions.moreApplicationsLoadedSuccess
  | typeof applicationApiActions.moreApplicationsLoadedAfterChangeSuccess
  | typeof applicationApiActions.moreApplicationsLoadedForSearchedApplicationSuccess;
type LoadOneOrMoreApplicationsFailureActionCreator =
  | typeof applicationApiActions.applicationsLoadedFailure
  | typeof applicationApiActions.applicationsLoadedForSearchedApplicationFailure
  | typeof applicationApiActions.moreApplicationsLoadedFailure
  | typeof applicationApiActions.moreApplicationsLoadedAfterChangeFailure
  | typeof applicationApiActions.moreApplicationsLoadedForSearchedApplicationFailure;
type LoadOneOrMoreApplicationsAction = ReturnType<
  LoadOneOrMoreApplicationsSuccessActionCreator | LoadOneOrMoreApplicationsFailureActionCreator
>;

const loadMoreApplications = (
  successAction: LoadOneOrMoreApplicationsSuccessActionCreator,
  failureAction: LoadOneOrMoreApplicationsFailureActionCreator,
  store = inject(Store),
  applicationResource = inject(ApplicationResource)
): OperatorFunction<
  {
    step: ApplicationStep;
    limit: number;
    offset: number;
  },
  LoadOneOrMoreApplicationsAction
> => {
  return (source) =>
    source.pipe(
      concatLatestFrom(() => [
        store.select(selectApplicationStatusesForSelectedAccount),
        store.select(selectVacancyIdFromRoute),
      ]),
      filter(hasVacancyIdInThirdPosition),
      map(([{ step, limit, offset }, statuses, vacancyId]) => ({
        statusId: getStatusIdForStep(step, statuses),
        vacancyId,
        offset,
        limit,
        // We can use statuses[0].accountId, because we are already filtering for selectedAccount
        accountId: statuses[0].accountId,
      })),
      switchMap(({ statusId, vacancyId, offset, limit, accountId }) =>
        applicationResource
          .fetchApplicationsByStatus({
            statusId,
            vacancyId,
            offset,
            limit,
            accountId,
          })
          .pipe(
            map((applications) => successAction({ applications })),
            catchError((error: unknown) =>
              of(failureAction({ errorResponse: getHttpErrorResponse(error) }))
            )
          )
      )
    );
};

const hasVacancyIdInThirdPosition = <T, V>(
  input: [T, V, string | undefined]
): input is [T, V, string] => input[2] !== undefined;

const hasVacancyInThirdPosition = <T, V>(
  input: [T, V, Vacancy | undefined]
): input is [T, V, Vacancy] => !!input[2] && input[2].id !== undefined;

// we don't want to refresh the count on every refresh success: only "changed" and "notfound" (in removeOnActions)
const getUpdateOneActionsForCountsTrigger = () =>
  updateOneActions.map((action) =>
    action.type === applicationApiActions.applicationRefreshSuccess.type
      ? applicationPageActions.applicationRefreshStatusChanged
      : action
  );

const isSearchedApplicationNotLoaded = ([{ applicationId, offset }, applications]: [
  ReturnType<typeof applicationPageActions.searchedApplicationSelected>,
  Application[],
]): boolean => applications[offset]?.id !== applicationId;
